import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Monitor, Tablet } from 'lucide-react';
import { useSession } from '../lib/session';
import { Icon } from './Icon';
import {
  getStoredPreference,
  setThemePreference,
} from '../lib/theme';
import { useLayout } from '../lib/layout';
import { BEER_CATALOG_ROLES, TIMECARD_ROLES } from '../lib/roles';

export function AppHeader(): React.ReactElement {
  const { instance } = useMsal();
  const { user, hasRole } = useSession();
  const [theme, setTheme] = useState(getStoredPreference);
  const { mode: layoutMode, toggle: toggleLayout } = useLayout();

  // Resolve the displayed icon: if stored pref is 'system', use media query to determine
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  function handleThemeToggle() {
    const next = isDark ? 'light' : 'dark';
    void setThemePreference(next);
    setTheme(next);
  }

  function handleSignOut() {
    void instance.logoutRedirect();
  }

  function navClass({ isActive }: { isActive: boolean }) {
    return `canto-nav-tab${isActive ? ' active' : ''}`;
  }

  return (
    <header className="app-header">
      {/* ── Left: brand ── */}
      <div className="app-header-left">
        <NavLink to="/" end className="brand-mark" aria-label="Canto home">
          <img
            className="logo logo-light"
            src="/brand/EB_Main_Logo_Black.png"
            alt="Ethereal Brewing"
          />
          <img
            className="logo logo-dark"
            src="/brand/EB_Main_Logo_White.png"
            alt="Ethereal Brewing"
          />
          <span className="brand-mark-text">CANTO</span>
        </NavLink>
      </div>

      {/* ── Nav ── */}
      <nav className="canto-nav" aria-label="Main navigation">
        <NavLink to="/" end className={navClass}>
          <Icon name="menu" size={16} /> Home
        </NavLink>
        <NavLink to="/taps" className={navClass}>
          <Icon name="taps" size={16} /> Tap list
        </NavLink>
        <NavLink to="/tap-history" className={navClass}>
          <Icon name="swap" size={16} /> Tap History
        </NavLink>
        <NavLink to="/handoff" className={navClass}>
          <Icon name="notes" size={16} /> Handoff
        </NavLink>
        <NavLink to="/timeslip" className={navClass}>
          <Icon name="clock" size={16} /> Timeslip
        </NavLink>
        <NavLink to="/events" className={navClass}>
          <Icon name="alert" size={16} /> Events
        </NavLink>
        <NavLink to="/reservations" className={navClass}>
          <Icon name="notes" size={16} /> Reservations
        </NavLink>
        {hasRole(...BEER_CATALOG_ROLES) && (
          <NavLink to="/beers" className={navClass}>
            <Icon name="book" size={16} /> Beers
          </NavLink>
        )}
        {hasRole('Admin', 'BarManager') && (
          <NavLink to="/bartenders" className={navClass}>
            <Icon name="user" size={16} /> Bartenders
          </NavLink>
        )}
        {hasRole(...TIMECARD_ROLES) && (
          <NavLink to="/timecards" className={navClass}>
            <Icon name="clock" size={16} /> Timecards
          </NavLink>
        )}
        {hasRole('Admin', 'Leader') && (
          <NavLink to="/ops" className={navClass}>
            <Icon name="filter" size={16} /> Admin
          </NavLink>
        )}
      </nav>

      {/* ── Right: layout + theme + user ── */}
      <div className="app-header-right">
        <button
          className="icon-btn"
          onClick={toggleLayout}
          aria-label={`Switch to ${layoutMode === 'touch' ? 'desktop' : 'touch'} layout`}
          title={layoutMode === 'touch' ? 'Switch to desktop layout' : 'Switch to touch / tablet layout'}
        >
          {layoutMode === 'touch' ? <Monitor size={18} /> : <Tablet size={18} />}
        </button>

        <button
          className="icon-btn"
          onClick={handleThemeToggle}
          aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        >
          <Icon name={isDark ? 'sun' : 'moon'} size={18} />
        </button>

        <div className="user-chip" title={user.displayName}>
          <div className="user-chip-avatar" aria-hidden="true">
            {userInitials(user.displayName)}
          </div>
          <span>{firstName(user.displayName)}</span>
        </div>

        <button className="btn btn-sm btn-ghost" onClick={handleSignOut}>
          Sign out
        </button>
      </div>
    </header>
  );
}

function userInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map((w) => w[0])
    .filter((c): c is string => Boolean(c))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function firstName(displayName: string): string {
  return displayName.split(' ')[0] ?? displayName;
}
